import { useMutation, useQuery } from '@apollo/client';
// @ts-ignore
import nestedProperty from 'nested-property';
import {
  adminInfoStoreQuery,
  productCategories as productCategoriesQuery,
} from '../../graphql/queries';
import React, { useEffect, useState } from 'react';
import { updateStore } from '../../graphql/mutations';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import { PreorderPartnerBanner } from '../../components/Banner/PreorderPartnerBanner';
import HelpPopover from '../../components/HelpPopover';
import { getObjectDiff } from '@/utils/objDiff';
import { centsToPrice } from '@/utils/formatter';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n from '@/utils/i18n';
import { Communicator } from '../../tools/Communication';
import { ContentSection } from '../../components/Layout/ContentSection';

type MerchantOnlineOrdersTabProps = {
  externalPickpackId: string | null;
  vytalStoreId: string | null;
  title: string;
};

export const MerchantOrdersSettingsTab: React.FC<
  MerchantOnlineOrdersTabProps
> = ({ externalPickpackId, vytalStoreId, title }) => {
  const [storeUpdate, setStoreUpdate] = useState<any | undefined>(null);
  const [oldStoreUpdate, setOldStoreUpdate] = useState<any | undefined>(null);
  const [updateStoreMutation, { loading: updating }] = useMutation(updateStore);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [defaultStockUserId, setDefaultStockUserId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    (async () => {
      if (vytalStoreId) {
        const response = await Communicator.Get('/Merchant/GetApiKeyInfo', {
          storeId: encodeURIComponent(vytalStoreId),
        });
        const body = await response.json();
        setApiKey(body.apiKey);
        setDefaultStockUserId(body.defaultStockUserId);
      }
    })();
  }, [vytalStoreId]);

  function resetStore(storeIn: any) {
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const { data = { store: null }, loading: loadingStore } = useQuery(
    adminInfoStoreQuery,
    {
      fetchPolicy: 'no-cache',
      skip: !externalPickpackId,
      variables: { id: externalPickpackId },
      onCompleted: (storeData) => {
        // @ts-ignore
        if (storeData) {
          resetStore(storeData.store);
        }
      },
    },
  );

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  const store: any = data.store;

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      const date = (target as EventTarget & HTMLInputElement).valueAsDate;
      if (date != null) {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(date);
      } else {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(new Date(0));
      }
    }

    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      nestedProperty.set(newStoreUpdate, name, formattedValue);
      return newStoreUpdate;
    });
  }

  function handleSave() {
    const getMutationInput = (storeIn: any) => {
      const lonlat = {
        longitude: Number(storeIn.lonlat.longitude),
        latitude: Number(storeIn.lonlat.latitude),
      };
      const mutationInput = {
        name: storeIn.name,
        address: {
          ...storeIn.address,
          __typename: undefined,
        },
        lonlat,
        opening_hours_multi: storeIn.opening_hours_multi_raw.map(
          (d: object) => ({
            ...d,
            __typename: undefined,
          }),
        ),
        special_opening_hours_multi: (
          storeIn.special_opening_hours_multi || []
        ).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        delivery_hours: (storeIn.delivery_hours || []).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        min_delivery_order_value: Number(storeIn.min_delivery_order_value),
        min_delivery_time: Number(storeIn.min_delivery_time),
        delivery_fee: Number(storeIn.delivery_fee),
        description_de: storeIn.description_de,
        description_en: storeIn.description_en,
        food_info_de: storeIn.description_de, // the fields food_info_(de|en) can be removed once the legacy App is not longer used
        food_info_en: storeIn.description_en,
        banner: storeIn.banner,
        phone_number: storeIn.phone_number,
        website: storeIn.website,
        min_order_time: Number(storeIn.min_order_time),
        cancelation_time: Number(storeIn.cancelation_time),
        preorder_sms_number: storeIn.preorder_sms_number,
      };
      return mutationInput;
    };

    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log('mutationInput', mutationInput);
    updateStoreMutation({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: store.id,
            filter: 'ALL',
          },
        },
      ],
      update: (cache, { data: { updateStore: updatedStore } }) => {
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert('Error updating store: ' + err);
    });
  }

  if (!externalPickpackId) return <p>externalPickpackId is not avaliable</p>;

  if (!store) return <p>Store {externalPickpackId} could not be loaded</p>;

  // @ts-ignore
  return (
    <ContentSection title={title} collapsable={false} key="content">
      {!storeUpdate.can_preorder && !storeUpdate.can_deliver && (
        <PreorderPartnerBanner
          cityId={storeUpdate.linked_city && storeUpdate.linked_city.id}
        />
      )}
      {(storeUpdate.can_preorder || storeUpdate.can_deliver) && (
        <EditSection
          title={i18n.t('Common:onlineOrders')}
          isLoading={updating}
          onSave={handleSave}
        >
          <EditItem title={i18n.t('OrderSetting:minimumTimes')}>
            {storeUpdate.can_preorder && (
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderSetting:minPreoderTime')}
                </span>
                <input
                  type="number"
                  className="form-control"
                  name="min_order_time"
                  autoComplete="off"
                  lang="en-US"
                  onChange={handleChange}
                  step="1"
                  value={storeUpdate.min_order_time || ''}
                  placeholder={i18n.t('OrderSetting:minPreoderTimeHint')}
                />
              </div>
            )}
            {storeUpdate.can_deliver && (
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderSetting:minDeliveryTime')}
                </span>
                <input
                  type="number"
                  className="form-control"
                  name="min_delivery_time"
                  autoComplete="off"
                  lang="en-US"
                  onChange={handleChange}
                  step="1"
                  value={storeUpdate.min_delivery_time || ''}
                  placeholder={i18n.t('OrderSetting:minDeliveryTimeHint')}
                />
              </div>
            )}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('OrderSetting:minCancellationTime')}
              </span>
              <input
                type="number"
                className="form-control"
                name="cancelation_time"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                step="1"
                value={storeUpdate.cancelation_time || ''}
                placeholder={i18n.t('OrderSetting:minCancellationTimeHint')}
              />
            </div>
          </EditItem>
          <EditItem title={i18n.t('OrderSetting:notifications')}>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('OrderSetting:textNotifications')}
              </span>
              <input
                type="text"
                className="form-control"
                name="preorder_sms_number"
                placeholder={i18n.t('Common:phonenumberExample')}
                value={storeUpdate.preorder_sms_number || ''}
                onChange={handleChange}
              />
              <HelpPopover
                text={i18n.t('OrderSetting:textNotificationsHelp')}
                id="sms-preorder-help"
              />
            </div>
          </EditItem>
          {storeUpdate.can_deliver && (
            <EditItem title={i18n.t('Common:delivery')}>
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderSetting:minDeliveryAmount')} (
                  {centsToPrice(
                    storeUpdate.min_delivery_order_value,
                    store.currency,
                  )}
                  )
                </span>
                <input
                  type="number"
                  className={'form-control'}
                  name="min_delivery_order_value"
                  autoComplete="off"
                  lang="en-US"
                  onChange={handleChange}
                  step="1"
                  value={storeUpdate.min_delivery_order_value}
                  placeholder={''}
                />
              </div>
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderSetting:deliveryFee')} (
                  {centsToPrice(storeUpdate.delivery_fee, store.currency)})
                </span>
                <input
                  type="number"
                  className={'form-control'}
                  name="delivery_fee"
                  autoComplete="off"
                  lang="en-US"
                  onChange={handleChange}
                  step="1"
                  value={storeUpdate.delivery_fee}
                  placeholder={''}
                />
              </div>
              <p>{i18n.t('OrderSetting:deliveryArea')}</p>
            </EditItem>
          )}
          <EditItem title={i18n.t('OrderSetting:apiKeyTitle')}>
            <p>{i18n.t('OrderSetting:apiKeyInfo')}</p>
            <div className="input-group mb-3">
              <span className="input-group-text" id="inputGroup-sizing-lg">
                {i18n.t('OrderSetting:apiKey')}
              </span>
              <input
                type="text"
                className="form-control"
                name="name"
                disabled={true}
                value={apiKey || ''}
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="inputGroup-sizing-lg">
                {i18n.t('OrderSetting:userId')}
              </span>
              <input
                type="text"
                className="form-control"
                name="name"
                disabled={true}
                value={defaultStockUserId || ''}
              />
            </div>
          </EditItem>
        </EditSection>
      )}
    </ContentSection>
  );
};
